import React, { useState } from "react";
import "./LivePlayerStats.css";
import { graphql, useLazyLoadQuery } from "react-relay";
import {
  sortFloats,
  sortGolfToPars,
  updateLocalMinMaxVal,
} from "../../../../../../Utils";
import LiveTournamentToolTable from "../LiveTournamentToolTable";
import StatTable from "../../../../../StatTable";
import DraftkingsInfoOverlay from "../../../../../DraftKingsInfoOverlay";

const STROKES_GAINED_FOR_STAT = graphql`
  query LivePlayerStatsStrokesGainedForStatQuery(
    $eventId: ID!
    $statKey: String!
  ) {
    liveStrokesGainedForStat(eventId: $eventId, statKey: $statKey) {
      rows {
        eventPlayer {
          player {
            id
            nameDisplay
            firstName
            lastName
            dkPlayer {
              id
            }
          }

          nextRoundSalary
        }

        toPar
        roundStats
      }

      nextRoundSalaryTitle
    }
  }
`;

export default function LivePlayerStats({
  selectedEventID,
  strokesGainedType,
  roundNumber,
  myPlayers,
}) {
  const [minMaxVals, setMinMaxVals] = useState({});
  const [selectedDkPlayerId, setSelectedDkPlayerId] = useState(undefined);

  let queryData;

  if (strokesGainedType === "overview") {
    return null;
  } else {
    queryData = useLazyLoadQuery(STROKES_GAINED_FOR_STAT, {
      eventId: selectedEventID,
      statKey: `strokes_gained_${strokesGainedType}`,
    });
  }

  const data = React.useMemo(() => {
    let localMinMax = {};

    const formattedData = queryData.liveStrokesGainedForStat.rows.map(
      (statsSummary) => {
        let newStatsSummary = { ...statsSummary };

        newStatsSummary.playerId = statsSummary.eventPlayer.player.id;
        newStatsSummary.playerName =
          statsSummary.eventPlayer.player.firstName +
          " " +
          statsSummary.eventPlayer.player.lastName;
        newStatsSummary.nextRoundSalary =
          statsSummary.eventPlayer.nextRoundSalary;

        if (statsSummary.eventPlayer.player.dkPlayer) {
          newStatsSummary.dkPlayerId =
            statsSummary.eventPlayer.player.dkPlayer.id;
        }

        newStatsSummary.roundStats.forEach((stat, index) => {
          let key = `round${index}`;

          // First value will be the sum of all rounds
          if (index === 0) {
            key = "eventTotal";
          }

          newStatsSummary[key] = stat;

          updateLocalMinMaxVal(localMinMax, key, parseFloat(stat));
        });

        return newStatsSummary;
      }
    );

    setMinMaxVals(localMinMax);

    return formattedData;
  }, [selectedEventID]);

  const columns = [
    {
      Header: "Name",
      accessor: "playerName",
      sortDescFirst: false,
    },
  ];

  if (queryData.liveStrokesGainedForStat.nextRoundSalaryTitle) {
    columns.push({
      Header: queryData.liveStrokesGainedForStat.nextRoundSalaryTitle,
      accessor: "nextRoundSalary",
      sortDescFirst: true,
    });
  }

  columns.push(
    ...[
      {
        Header: "To Par",
        accessor: "toPar",
        sortType: sortGolfToPars,
        sortInverted: true,
      },
      {
        Header: "Event",
        accessor: "eventTotal",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "Rd 1",
        accessor: "round1",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "Rd 2",
        accessor: "round2",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "Rd 3",
        accessor: "round3",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "Rd 4",
        accessor: "round4",
        sortInverted: false,
        sortType: sortFloats,
      },
    ]
  );

  const myPlayerIds = myPlayers ? myPlayers.map((player) => player.value) : [];

  function showDKPlayerInfo(dkPlayerId) {
    if (dkPlayerId) {
      setSelectedDkPlayerId(dkPlayerId);
    }
  }

  function hideDKPlayerInfo() {
    setSelectedDkPlayerId(undefined);
  }

  return (
    <div className="pga-scorecards-container">
      <DraftkingsInfoOverlay
        show={selectedDkPlayerId !== undefined}
        playerId={selectedDkPlayerId}
        close={hideDKPlayerInfo}
      />
      <LiveTournamentToolTable
        data={data}
        columns={columns}
        roundNumber={roundNumber}
        myPlayerIds={myPlayerIds}
        showDrawer={false}
        minMaxVals={minMaxVals}
        showDKPlayerInfo={showDKPlayerInfo}
      />
    </div>
  );
}
